import { Injectable } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    CanActivate,
    CanMatch,
    Route,
    Router,
    RouterStateSnapshot,
    UrlSegment,
    UrlTree,
} from '@angular/router';
import { LoggingService } from '@app/core/services';
import { AuthService } from '@modules/auth/services';
import { APP_ROUTES } from '@modules/navigation/models';

const ACCESS_DENIED_ROUTE = APP_ROUTES.ACCESS_DENIED;

@Injectable({
    providedIn: 'root',
})
export class AuthGuard implements CanActivate, CanMatch {
    constructor(
        private _authService: AuthService,
        private router: Router,
        private loggingService: LoggingService
    ) {}

    async canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
        return this.evaluateAccess(state.url, true);
    }

    async canMatch(_route: Route, segments: UrlSegment[]): Promise<boolean | UrlTree> {
        const url = `/${segments.map((segment) => segment.path).join('/')}`;
        return this.evaluateAccess(url, false);
    }

    private async evaluateAccess(url: string, requireSecurityContext: boolean): Promise<boolean | UrlTree> {
        this.loggingService.debug('[AuthGuard] Checking authenticated route.', { url });

        if (await this._authService.isLoggedIn()) {
            if (!requireSecurityContext) {
                return true;
            }

            const securityContextLoaded = await this._authService.ensureSecurityContextLoaded();
            this.loggingService.debug('[AuthGuard] Authenticated user detected.', {
                securityContextLoaded,
                url,
            });

            if (!securityContextLoaded) {
                if (url === ACCESS_DENIED_ROUTE) {
                    return true;
                }

                this.loggingService.warn('[AuthGuard] Authenticated route blocked because platform_mode is unavailable.', {
                    url,
                });
                return this.router.parseUrl(ACCESS_DENIED_ROUTE);
            }

            if (!this._authService.isDomainAllowed()) {
                this.loggingService.warn('[AuthGuard] Domain claim does not match current hostname. Signing out.', {
                    url,
                    hostname: window.location.hostname,
                });
                this._authService.logout();
                return false;
            }

            return true;
        }

        this.loggingService.info('[AuthGuard] No valid session; redirecting to login.', { url });
        return this.router.parseUrl('/auth/login');
    }
}
